import React, { useLayoutEffect, useRef, useState } from "react";
import { decode } from "he";
import { strings } from "@perceptron/constants";
import { ANNOTATION_COLORS } from "@perceptron/constants/colors";
import type { AnnotationRendererProps } from "../types";
import { boxStyle } from "../styles";
import { TrackingSourceTag, useTrackingSourceAppearance } from "../../../tracking/inspector/sourceAppearance";
import type { VideoLayout } from "../../../utils/videoLayout";

interface BoxRendererProps extends AnnotationRendererProps {
  focused?: boolean;
  pinned?: boolean;
  showLabel?: boolean;
  videoLayout: VideoLayout;
}

const LABEL_GAP = 4;

export const BoxRenderer: React.FC<BoxRendererProps> = React.memo(
  ({ annotation, bounds, onInteraction, focused, pinned, showLabel, videoLayout }) => {
    const hoverId = annotation.interactionId ?? annotation.id;
    const clickId = annotation.id;
    const appearance = useTrackingSourceAppearance(
      annotation.type === "point_box" ? (annotation.trackingMethod ?? "anchor") : undefined,
    );
    const label = decode(annotation.label ?? "");
    const hasLabel = focused && showLabel && !!label;
    const borderWidth = Math.max(0, Math.min(boxStyle.borderWidth, bounds.width / 2, bounds.height / 2));
    const labelRef = useRef<HTMLSpanElement>(null);
    const [labelSize, setLabelSize] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
      const element = labelRef.current;
      if (!element) return;
      const measure = () => {
        // Layout dimensions stay in video coordinates even when a parent is scaled.
        const width = element.offsetWidth;
        const height = element.offsetHeight;
        setLabelSize((previous) =>
          previous.width === width && previous.height === height ? previous : { width, height },
        );
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }, [hasLabel, label, videoLayout.width]);

    const below = bounds.y - videoLayout.offsetY < labelSize.height + LABEL_GAP;
    const inset = below ? borderWidth + LABEL_GAP : 0;
    const edgePadding = below ? LABEL_GAP : 0;
    const left = bounds.x + inset;
    const maxLeft = videoLayout.offsetX + videoLayout.width - edgePadding - labelSize.width;
    // Prefer the top-left corner, moving to the right corner only on actual overflow.
    const labelX = Math.max(
      videoLayout.offsetX + edgePadding,
      Math.min(left <= maxLeft ? left : bounds.x + bounds.width - inset - labelSize.width, maxLeft),
    );
    const labelY = Math.max(
      videoLayout.offsetY,
      Math.min(
        below ? bounds.y + inset : bounds.y - labelSize.height - LABEL_GAP,
        videoLayout.offsetY + videoLayout.height - labelSize.height,
      ),
    );

    const style: React.CSSProperties = {
      ...boxStyle,
      borderWidth,
      ...(appearance && { borderColor: appearance.color }),
      transform: `translate(${bounds.x}px, ${bounds.y}px)`,
      width: Math.max(0, bounds.width),
      height: Math.max(0, bounds.height),
      // The mask is at z=1. Keep all outlines above it, with focused labels on top.
      zIndex: focused ? 3 : 2,
      backgroundColor: "transparent",
    };

    return (
      <>
        <div
          aria-hidden="true"
          data-video-box-fill={clickId}
          style={{
            ...style,
            borderWidth: 0,
            pointerEvents: "none",
            zIndex: 0,
            backgroundColor: focused ? "transparent" : appearance ? `${appearance.color}1a` : boxStyle.backgroundColor,
          }}
        />
        <div
          aria-label={label || strings.imageDialog.boundingBox}
          aria-pressed={pinned ?? false}
          className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          data-annotation-id={clickId}
          data-tracking-method={appearance?.method}
          data-video-focused={focused || undefined}
          role="button"
          style={style}
          tabIndex={0}
          onBlur={() => onInteraction({ type: "mouseleave", annotationId: hoverId })}
          onClick={(event) => {
            event.stopPropagation();
            onInteraction({ type: "click", annotationId: clickId });
          }}
          onFocus={() => onInteraction({ type: "mouseenter", annotationId: hoverId })}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.stopPropagation();
            onInteraction({ type: "click", annotationId: clickId });
          }}
          onMouseEnter={() => onInteraction({ type: "mouseenter", annotationId: hoverId })}
          onMouseLeave={() => onInteraction({ type: "mouseleave", annotationId: hoverId })}
        >
          {hasLabel && (
            <span
              className="pointer-events-none absolute w-max truncate rounded-sm px-2 py-0.5 text-xs text-white"
              data-video-focus-label=""
              ref={labelRef}
              style={{
                backgroundColor: ANNOTATION_COLORS.PRIMARY,
                maxWidth: Math.max(0, videoLayout.width - 2 * LABEL_GAP),
                // Absolute children are positioned from the box's inner border edge.
                left: labelX - bounds.x - borderWidth,
                top: labelY - bounds.y - borderWidth,
              }}
            >
              {label}
            </span>
          )}
          {appearance && (
            <TrackingSourceTag
              alignRight={annotation.type === "point_box" && annotation.coordinates.top_left.x > 500}
              assisted={annotation.type === "point_box" && !!annotation.recovery}
              below={hasLabel || bounds.y - videoLayout.offsetY < 28}
              className={hasLabel && below ? "mt-8" : undefined}
              method={appearance.method}
            />
          )}
        </div>
      </>
    );
  },
);

BoxRenderer.displayName = "BoxRenderer";
